/**
 * @fileoverview Markdown processor hook for text editor
 * 
 * Provides markdown processing capabilities for preview mode
 */

import { useMemo, useCallback } from 'react';

/**
 * Markdown processor hook
 * 
 * @param {boolean} enableMarkdown - Whether markdown processing is enabled
 * @returns {Object} Markdown processor utilities
 */
export function useMarkdownProcessor(enableMarkdown) {
  
  /**
   * Simple markdown processor
   * Since we don't have a markdown library, we'll implement basic processing
   */
  const processor = useMemo(() => {
    if (!enableMarkdown) return null;

    return {
      /**
       * Process markdown content to HTML
       */
      process: (content) => {
        if (!content) return '';

        let html = content;

        // Headers
        html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
        html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
        html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

        // Bold
        html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

        // Italic
        html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
        html = html.replace(/_(.*?)_/g, '<em>$1</em>');

        // Code blocks
        html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
        
        // Inline code
        html = html.replace(/`(.*?)`/g, '<code>$1</code>');

        // Links
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

        // Lists
        html = html.replace(/^\* (.+)$/gm, '<li>$1</li>');
        html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
        html = html.replace(/^\+ (.+)$/gm, '<li>$1</li>');
        
        // Wrap consecutive list items in ul tags
        html = html.replace(/(<li>.*<\/li>)/gs, (match) => {
          return `<ul>${match}</ul>`;
        });

        // Numbered lists
        html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
        html = html.replace(/(<li>.*<\/li>)/gs, (match, p1) => {
          // Only wrap if it's not already wrapped in ul
          if (!match.includes('<ul>')) {
            return `<ol>${match}</ol>`;
          }
          return match;
        });

        // Line breaks and paragraphs
        html = html.replace(/\n\n/g, '</p><p>');
        html = html.replace(/\n/g, '<br>');
        
        // Wrap in paragraphs if not already wrapped
        if (!html.startsWith('<')) {
          html = `<p>${html}</p>`;
        }

        // Clean up empty paragraphs
        html = html.replace(/<p><\/p>/g, '');
        html = html.replace(/<p>\s*<\/p>/g, '');

        return html;
      },

      /**
       * Strip markdown formatting to get plain text
       */
      stripMarkdown: (content) => {
        if (!content) return '';

        return content
          .replace(/^#{1,6}\s+/gm, '') // Headers
          .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
          .replace(/__(.*?)__/g, '$1') // Bold
          .replace(/\*(.*?)\*/g, '$1') // Italic
          .replace(/_(.*?)_/g, '$1') // Italic
          .replace(/```[\s\S]*?```/g, '') // Code blocks
          .replace(/`(.*?)`/g, '$1') // Inline code
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
          .replace(/^\s*[-*+]\s+/gm, '') // List items
          .replace(/^\s*\d+\.\s+/gm, '') // Numbered list items
          .trim();
      }
    };
  }, [enableMarkdown]);

  /**
   * Process content with error handling
   */
  const processContent = useCallback((content) => {
    if (!processor) return content;

    try {
      return processor.process(content);
    } catch (error) {
      console.warn('Markdown processing error:', error);
      return `<pre>${content}</pre>`;
    }
  }, [processor]);

  /**
   * Strip markdown with error handling
   */
  const stripMarkdown = useCallback((content) => {
    if (!processor) return content;

    try {
      return processor.stripMarkdown(content);
    } catch (error) {
      console.warn('Markdown stripping error:', error);
      return content;
    }
  }, [processor]);

  /**
   * Check if content contains markdown
   */
  const hasMarkdown = useCallback((content) => {
    if (!content || !enableMarkdown) return false;

    const markdownPatterns = [
      /^#{1,6}\s+/m, // Headers
      /\*\*.*?\*\*/, // Bold
      /__.*?__/, // Bold
      /\*.*?\*/, // Italic
      /_.*?_/, // Italic
      /```[\s\S]*?```/, // Code blocks
      /`.*?`/, // Inline code
      /\[.*?\]\(.*?\)/, // Links
      /^\s*[-*+]\s+/m, // List items
      /^\s*\d+\.\s+/m // Numbered list items
    ];

    return markdownPatterns.some(pattern => pattern.test(content));
  }, [enableMarkdown]);

  return {
    process: processContent,
    stripMarkdown,
    hasMarkdown,
    isEnabled: enableMarkdown,
    processor
  };
}